import { useEffect, useRef, useState } from 'react';
import { Size, t } from '@bms2/shared';
import { Button } from './Button';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * Copy Field (T731): a value a person carries to another site, drawn read-only
 * with a Copy button beside it. Copy writes the value to the clipboard and the
 * button reads Copied for a moment. 🔨 Where the browser offers no clipboard, or
 * refuses the write, the value is selected instead, so a keyboard copy still
 * takes it whole.
 *
 * The box is as wide as the value and the button, since the value's length is
 * exactly known (decision 0297); a narrower column shrinks it, and the field
 * scrolls the rest.
 */
export interface CopyFieldProps {
  label: string;
  value: string;
  /** The field's own name, which the wrapper carries as `data-copy-field` for a page's tests. */
  name: string;
  hint?: string;
}

/** The states the Component Test Page draws: a short value, and a long address that runs past a narrow column (piece 3). */
export const copyFieldDocumentedStates = ['short', 'long'] as const;

/** How long the button reads Copied before it reads Copy again. */
export const COPY_FIELD_COPIED_MS = 2000;

/** The characters the box keeps beside the value for the button. */
const BUTTON_CHARS = 10;

export function CopyField({ label, value, name, hint }: CopyFieldProps) {
  // 🔨 Both outcomes redraw the button: Copied, or Selected where the clipboard is absent or refused the write, so a
  // person (and the every-button walk, which failed landing 44545d64 on a silent fallback) sees that the press did something.
  const [outcome, setOutcome] = useState<'idle' | 'copied' | 'selected'>('idle');
  const copied = outcome === 'copied';
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (outcome === 'idle') return undefined;
    const timer = setTimeout(() => setOutcome('idle'), COPY_FIELD_COPIED_MS);
    return () => clearTimeout(timer);
  }, [outcome]);

  const selectValue = () => {
    input.current?.select();
    setOutcome('selected');
  };
  const copy = () => {
    const clipboard = typeof navigator === 'undefined' ? null : navigator.clipboard;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      selectValue();
      return;
    }
    clipboard.writeText(value).then(() => setOutcome('copied'), /* silent: a refused clipboard falls back to selecting the text to copy by hand. */ selectValue);
  };
  const buttonText = outcome === 'copied' ? t('copyField.copied') : outcome === 'selected' ? t('copyField.selected') : t('copyField.copy');

  return (
    <div data-copy-field={name} data-copied={copied ? 'true' : 'false'} data-copy-outcome={outcome}>
      <FieldShell label={label} hint={hint ?? null} chars={Math.max(value.length, 1) + BUTTON_CHARS}>
        {({ inputId }) => (
          <>
            <input
              id={inputId}
              ref={input}
              className={`${styles.input} ${styles.mono}`}
              type="text"
              name={name}
              value={value}
              readOnly
              onFocus={(event) => event.target.select()}
            />
            <Button size={Size.Small} text={[buttonText]} actions={[copy]} />
          </>
        )}
      </FieldShell>
    </div>
  );
}
